import { z } from 'zod';
import { amountSchema } from '../common/amount.js';
import { paginationQuerySchema } from '../common/pagination.js';
import { localeSchema } from '../common/primitives.js';
import { branchRefSchema, dateOnlySchema, employeeRefSchema } from '../common/refs.js';
import { holderRefSchema, ledgerEntryTypeSchema } from '../holders/contract.js';
import { exportFormatSchema, moneyTotalsSchema } from '../transfers/contract.js';
import { transferDirectionSchema } from '../transfers/direction.js';
/**
 * Reports — التقارير (transfers.md section 9). Filters: period, branch,
 * employee, box, bank account, customer (name or phone). Cancelled transfers
 * are reported separately and excluded from totals. Balances are recorded
 * movements, not bank readings. Data is limited to the user's branches.
 */
export const reportFilterSchema = z.object({
    from: dateOnlySchema.optional(),
    to: dateOnlySchema.optional(),
    branchId: z.string().optional(),
    employeeId: z.string().optional(),
    boxId: z.string().optional(),
    bankAccountId: z.string().optional(),
    /** Customer name or phone, sender or receiver. */
    customer: z.string().trim().max(200).optional(),
    /** اتجاه الحوالة (TRF-47). */
    direction: transferDirectionSchema.optional(),
});
export const REPORT_KINDS = [
    'detailed',
    'summary',
    'by-branch',
    'by-employee',
    'by-customer',
    'holders',
];
export const reportKindSchema = z.enum(REPORT_KINDS);
/** GET /reports/detailed — الحركات التفصيلية, the list columns, paginated. */
export const detailedReportQuerySchema = paginationQuerySchema.extend(reportFilterSchema.shape);
/** GET /reports/summary — ملخص الحوالات. `commission` is إجمالي العمولات (service income). */
export const summaryReportSchema = z.object({
    posted: moneyTotalsSchema,
    cancelled: moneyTotalsSchema,
});
const groupedRow = (key) => z.object({
    key,
    posted: moneyTotalsSchema,
    cancelledCount: z.number().int().nonnegative(),
});
/** GET /reports/by-branch */
export const byBranchReportSchema = z.object({
    rows: z.array(groupedRow(branchRefSchema)),
    total: moneyTotalsSchema,
});
/** GET /reports/by-employee (the employee who posted). */
export const byEmployeeReportSchema = z.object({
    rows: z.array(groupedRow(employeeRefSchema)),
    total: moneyTotalsSchema,
});
/**
 * GET /reports/by-customer — grouped by phone. A transfer where the sender is
 * the receiver counts once for that customer.
 */
export const byCustomerReportSchema = z.object({
    rows: z.array(groupedRow(z.object({ phone: z.string(), name: z.string(), customerId: z.string().nullable() })).extend({
        asSenderCount: z.number().int().nonnegative(),
        asReceiverCount: z.number().int().nonnegative(),
    })),
});
/**
 * GET /reports/holders — حركة ورصيد الصناديق والحسابات: per holder, the
 * balance at the start of the period, movements in and out by type, and the
 * closing balance. A bank account shared by branches appears once.
 */
export const holdersReportQuerySchema = reportFilterSchema.pick({
    from: true,
    to: true,
    branchId: true,
    boxId: true,
    bankAccountId: true,
});
const byTypeSchema = z.record(ledgerEntryTypeSchema, amountSchema);
export const holdersReportRowSchema = z.object({
    holder: holderRefSchema,
    branches: z.array(branchRefSchema),
    openingBalance: amountSchema,
    inByType: byTypeSchema,
    outByType: byTypeSchema,
    totalIn: amountSchema,
    totalOut: amountSchema,
    closingBalance: amountSchema,
});
export const holdersReportSchema = z.object({
    rows: z.array(holdersReportRowSchema),
    total: z.object({
        openingBalance: amountSchema,
        totalIn: amountSchema,
        totalOut: amountSchema,
        closingBalance: amountSchema,
    }),
});
/**
 * GET /reports/:kind/export?format=xlsx|pdf&locale=ar|en plus the filters.
 * Excel keeps phones and references as text and never writes customer input
 * as a formula; the PDF renders Arabic with the report name, period, filters
 * and totals (TRF-29).
 */
export const reportExportQuerySchema = reportFilterSchema.extend({
    format: exportFormatSchema,
    locale: localeSchema.default('ar'),
});
//# sourceMappingURL=contract.js.map